import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Globe, Loader2, AlertCircle, Check, Plus, Trash2, Package } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspaceUsage } from "@/hooks/useWorkspaceUsage";
import { BulkImportDialog } from "./BulkImportDialog";
import { useUserRole } from "@/hooks/useUserRole";

interface WebScrapeImporterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onProductImported: () => void;
}

interface ProductAttribute {
  label: string;
  value: string;
}

interface ExtractedProduct {
  name: string;
  description?: string;
  price?: number;
  sku?: string;
  colors?: string[];
  dimensions?: string;
  additional_attributes?: ProductAttribute[];
}

interface DiscoveredProduct {
  url: string;
  name: string;
  price: number | null;
  image_url: string | null;
}

export function WebScrapeImporter({ open, onOpenChange, workspaceId, onProductImported }: WebScrapeImporterProps) {
  const { toast } = useToast();
  const { usageData, refetch } = useWorkspaceUsage(workspaceId);
  const { isOwner } = useUserRole();
  
  const [url, setUrl] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [extractedProduct, setExtractedProduct] = useState<ExtractedProduct | null>(null);
  const [editedProduct, setEditedProduct] = useState<ExtractedProduct | null>(null);
  
  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [discoveredProducts, setDiscoveredProducts] = useState<DiscoveredProduct[]>([]);
  const [siteName, setSiteName] = useState("");

  const scrapesUsed = usageData?.usage?.scrapes ?? 0;
  const scrapesLimit = usageData?.limits?.scrapes ?? 2;
  const canScrape = isOwner || scrapesUsed < scrapesLimit;

  const handleDiscoverProducts = async () => {
    if (!url.trim()) {
      toast({
        title: "URL obrigatória",
        description: "Insira a URL do site ou catálogo.",
        variant: "destructive",
      });
      return;
    }

    setIsDiscovering(true);

    try {
      const { data, error } = await supabase.functions.invoke("discover-product-urls", {
        body: { url, workspaceId },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      if (data.data?.product_urls?.length > 0) {
        setDiscoveredProducts(data.data.product_urls);
        setSiteName(data.data.site_name || "Site");
        setShowBulkImport(true);
        toast({
          title: `${data.data.product_urls.length} produto(s) encontrado(s)!`,
          description: "Selecione quais deseja importar.",
        });
      } else {
        toast({
          title: "Nenhum produto encontrado",
          description: "Tente usar uma URL de produto específico ou verifique o site.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error discovering products:", error);
      toast({
        title: "Erro ao descobrir produtos",
        description: error instanceof Error ? error.message : "Não foi possível analisar o site.",
        variant: "destructive",
      });
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleExtract = async () => {
    if (!url.trim()) {
      toast({
        title: "URL obrigatória",
        description: "Insira a URL do produto que deseja importar.",
        variant: "destructive",
      });
      return;
    }

    if (!canScrape) {
      toast({
        title: "Limite atingido",
        description: `Você já usou ${scrapesLimit} importações este mês.`,
        variant: "destructive",
      });
      return;
    }

    setIsExtracting(true);
    setExtractedProduct(null);
    setEditedProduct(null);

    try {
      const { data, error } = await supabase.functions.invoke("scrape-product", {
        body: { url, workspaceId },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      // Multi-product response (e.g. Prefiro Delivery): redirect to bulk import flow
      if (Array.isArray(data.products) && data.products.length > 0) {
        const discovered: DiscoveredProduct[] = data.products.map((p: any) => ({
          url: p.product_url || url,
          name: p.name,
          price: p.price ?? null,
          image_url: p.image_url ?? null,
        }));
        setDiscoveredProducts(discovered);
        setSiteName("Catálogo");
        setShowBulkImport(true);
        toast({
          title: `${discovered.length} produto(s) encontrado(s) no catálogo!`,
          description: "Selecione quais deseja importar.",
        });
      } else if (data.product) {
        setExtractedProduct(data.product);
        setEditedProduct(data.product);
        refetch();
        toast({
          title: "Dados extraídos!",
          description: "Revise os dados antes de importar.",
        });
      }
    } catch (error) {
      console.error("Error extracting product:", error);
      toast({
        title: "Erro na extração",
        description: error instanceof Error ? error.message : "Não foi possível extrair os dados do produto.",
        variant: "destructive",
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!editedProduct?.name) {
      toast({
        title: "Nome obrigatório",
        description: "O produto precisa ter um nome.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      // Build sync_metadata with additional attributes
      const syncMetadata = editedProduct.additional_attributes?.length
        ? { attributes: editedProduct.additional_attributes }
        : null;

      // Insert product
      const { data: savedProduct, error: insertError } = await supabase
        .from("products")
        .insert({
          workspace_id: workspaceId,
          name: editedProduct.name,
          description: editedProduct.description || null,
          price: editedProduct.price || null,
          sku: editedProduct.sku || null,
          colors: editedProduct.colors || null,
          dimensions: editedProduct.dimensions || null,
          type: "product",
          source: "web_scrape",
          is_active: true,
          sync_metadata: syncMetadata as any,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Enqueue embedding for RAG (server-side)
      try {
        await supabase.functions.invoke("enqueue-product-embedding", {
          body: { productId: savedProduct.id },
        });
      } catch (embeddingError) {
        console.warn("Warning: Failed to enqueue embedding:", embeddingError);
        toast({
          title: "Atenção",
          description: "Produto salvo, mas indexação para IA ficou pendente. Tente reindexar depois.",
        });
      }

      toast({
        title: "Produto importado!",
        description: `"${editedProduct.name}" foi adicionado com sucesso.`,
      });

      // Reset and close
      setUrl("");
      setExtractedProduct(null);
      setEditedProduct(null);
      onProductImported();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving product:", error);
      toast({
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Não foi possível salvar o produto.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setExtractedProduct(null);
    setEditedProduct(null);
    setDiscoveredProducts([]);
    setSiteName("");
    onOpenChange(false);
  };

  const handleBulkImportClose = () => {
    setShowBulkImport(false);
    setDiscoveredProducts([]);
    setSiteName("");
  };

  const handleBulkImportComplete = () => {
    refetch();
    onProductImported();
    handleClose();
  };

  const updateField = (field: keyof ExtractedProduct, value: string | number | string[] | ProductAttribute[] | undefined) => {
    if (editedProduct) {
      setEditedProduct({ ...editedProduct, [field]: value });
    }
  };

  const updateAttribute = (index: number, field: "label" | "value", newValue: string) => {
    if (!editedProduct?.additional_attributes) return;
    const updatedAttrs = [...editedProduct.additional_attributes];
    updatedAttrs[index] = { ...updatedAttrs[index], [field]: newValue };
    updateField("additional_attributes", updatedAttrs);
  };

  const addAttribute = () => {
    const currentAttrs = editedProduct?.additional_attributes || [];
    updateField("additional_attributes", [...currentAttrs, { label: "", value: "" }]);
  };

  const removeAttribute = (index: number) => {
    if (!editedProduct?.additional_attributes) return;
    const updatedAttrs = editedProduct.additional_attributes.filter((_, i) => i !== index);
    updateField("additional_attributes", updatedAttrs.length > 0 ? updatedAttrs : undefined);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Importar de URL
            </DialogTitle>
            <DialogDescription>
              Extraia dados de produtos de qualquer página da web ou importe vários produtos de um catálogo.
            </DialogDescription>
          </DialogHeader>

          {/* Usage indicator */}
          <div className="p-3 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Importações usadas este mês</span>
              <span className={`font-medium ${!canScrape ? "text-destructive" : ""}`}>
                {scrapesUsed} / {scrapesLimit}
              </span>
            </div>
            <Progress value={(scrapesUsed / scrapesLimit) * 100} className="h-2" />
            {!canScrape && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Limite atingido. Aguarde o próximo período.
              </p>
            )}
          </div>

          {/* URL input */}
          {!extractedProduct && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL do Produto ou Catálogo</Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://loja.com.br/produto ou https://loja.com.br"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={isExtracting || isDiscovering}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={handleExtract} 
                  disabled={isExtracting || isDiscovering || !canScrape || !url.trim()}
                  variant="default"
                >
                  {isExtracting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Extraindo...
                    </>
                  ) : (
                    <>
                      <Globe className="w-4 h-4 mr-2" />
                      1 Produto
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={handleDiscoverProducts} 
                  disabled={isExtracting || isDiscovering || !url.trim()}
                  variant="outline"
                >
                  {isDiscovering ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-2" />
                      Vários Produtos
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Use "1 Produto" para URL de produto específico ou "Vários Produtos" para importar de um catálogo/site
              </p>
            </div>
          )}

          {/* Extracted product preview */}
          {editedProduct && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-green-500" />
                Dados extraídos - edite se necessário
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={editedProduct.name || ""}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={editedProduct.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="price">Preço (R$)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={editedProduct.price || ""}
                      onChange={(e) => updateField("price", e.target.value ? parseFloat(e.target.value) : undefined)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU</Label>
                    <Input
                      id="sku"
                      value={editedProduct.sku || ""}
                      onChange={(e) => updateField("sku", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="colors">Cores (separadas por vírgula)</Label>
                  <Input
                    id="colors"
                    value={editedProduct.colors?.join(", ") || ""}
                    onChange={(e) => updateField("colors", e.target.value ? e.target.value.split(",").map(c => c.trim()).filter(Boolean) : undefined)}
                    placeholder="Preto, Branco, Azul"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dimensions">Dimensões</Label>
                  <Input
                    id="dimensions"
                    value={editedProduct.dimensions || ""}
                    onChange={(e) => updateField("dimensions", e.target.value)}
                    placeholder="30cm x 20cm x 10cm"
                  />
                </div>

                {/* Additional attributes section */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Atributos Adicionais</Label>
                    <Button variant="outline" size="sm" onClick={addAttribute} type="button">
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  
                  {editedProduct.additional_attributes && editedProduct.additional_attributes.length > 0 ? (
                    <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                      {editedProduct.additional_attributes.map((attr, index) => (
                        <div key={index} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center">
                          <Input
                            value={attr.label}
                            onChange={(e) => updateAttribute(index, "label", e.target.value)}
                            placeholder="Nome"
                            className="text-sm"
                          />
                          <Input
                            value={attr.value}
                            onChange={(e) => updateAttribute(index, "value", e.target.value)}
                            placeholder="Valor"
                            className="text-sm"
                          />
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => removeAttribute(index)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Nenhum atributo adicional extraído. Clique em "Adicionar" para incluir informações como peso, ingredientes, etc.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            {extractedProduct && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Importar Produto"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={showBulkImport}
        onOpenChange={handleBulkImportClose}
        workspaceId={workspaceId}
        siteName={siteName}
        products={discoveredProducts}
        onImportComplete={handleBulkImportComplete}
      />
    </>
  );
}
